import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { monthKey, monthLong, syncedLabel } from '@/shared/lib';
import { Icon, IconButton, Segmented, Switch } from '@/shared/ui';
import type { Billing, Offer } from '../api/types';
import { billingSet, compareSet, offerSet, rangeSet, unitSet } from '../model/filtersSlice';
import type { RevenueUnit } from '../model/prefsSlice';
import type { Period } from '../model/usePeriod';

const UNITS = [
  { value: 'MRR', label: 'MRR' },
  { value: 'ARR', label: 'ARR' },
] as const satisfies readonly { value: RevenueUnit; label: string }[];

/**
 * Header toolbar: range picker, compare switch, MRR/ARR, offer and billing (`.toolbar[data-filters]`).
 * `periodOnly` keeps just the range picker, for pages the revenue filters do not apply to.
 */
export function FilterBar({
  period,
  periodOnly = false,
}: {
  period: Period;
  periodOnly?: boolean;
}) {
  const dispatch = useAppDispatch();
  const { f, n, compare, compareAvailable } = period;
  if (periodOnly) {
    return (
      <div className="toolbar" data-filters="">
        <RangePicker period={period} />
      </div>
    );
  }

  return (
    <div className="toolbar" data-filters="">
      <RangePicker period={period} />
      <Switch
        id="compare"
        checked={compare}
        disabled={!compareAvailable}
        onChange={() => dispatch(compareSet(!period.compareChosen))}
        label="Compare to previous period"
        hint={
          compareAvailable
            ? `vs ${monthLong(f - n)} – ${monthLong(f - 1)}`
            : 'No data for the previous period'
        }
      />
      <span className="toolbar__spacer" />
      <Segmented
        label="Revenue unit"
        options={UNITS}
        value={period.unit}
        onChange={(unit) => dispatch(unitSet(unit))}
      />
      <label className="visually-hidden" htmlFor="offer">
        Product offer
      </label>
      <select
        className={
          period.scope.offer === 'all'
            ? 'select select--compact'
            : 'select select--compact is-active'
        }
        id="offer"
        value={period.scope.offer}
        onChange={(event) => dispatch(offerSet(event.target.value as Offer | 'all'))}
      >
        <option value="all">All product offers</option>
        <option value="Business">Business</option>
        <option value="Non-Profit">Non-Profit</option>
        <option value="Enterprise">Enterprise</option>
      </select>
      <label className="visually-hidden" htmlFor="billing">
        Billing type
      </label>
      <select
        className={
          period.scope.billing === 'all'
            ? 'select select--compact'
            : 'select select--compact is-active'
        }
        id="billing"
        value={period.scope.billing}
        onChange={(event) => dispatch(billingSet(event.target.value as Billing | 'all'))}
      >
        <option value="all">All billing types</option>
        <option value="Yearly">Stripe · yearly</option>
        <option value="Monthly">Stripe · monthly</option>
        <option value="Enterprise contract">Enterprise contract</option>
      </select>
    </div>
  );
}

function RangePicker({ period }: { period: Period }) {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const { f, t, current: N, earliest } = period;
  const ytd = N - (N % 12);
  const presets: [string, number, number][] = [
    ['Last 3 months', N - 2, N],
    ['Last 6 months', N - 5, N],
    ['Last 12 months', N - 11, N],
    ['Year to date', ytd, N],
    ['Last 24 months', N - 23, N],
    ['Previous year', ytd - 12, ytd - 1],
  ];

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const setRange = (from: number, to: number) => {
    dispatch(rangeSet({ from: monthKey(Math.max(earliest, from)), to: monthKey(Math.min(N, to)) }));
  };
  const shift = (edge: 'from' | 'to', step: number) => {
    if (edge === 'from') setRange(Math.max(earliest, Math.min(f + step, t)), t);
    else setRange(f, Math.min(N, Math.max(t + step, f)));
  };

  return (
    <div className="range" ref={wrapper}>
      <button
        ref={button}
        className="range__btn"
        id="range-btn"
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="range-pop"
        title={
          t === N
            ? `${monthLong(N)} is month to date: Stripe data until ${syncedLabel(period.meta.stripeSyncedAt)} UTC`
            : undefined
        }
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        <Icon name="calendar" size="sm" />
        <span>{`${monthLong(f)} – ${monthLong(t)}${t === N ? ' (MTD)' : ''}`}</span>
        <Icon name="chevron" size="xs" />
      </button>
      <div className="popover" id="range-pop" role="dialog" aria-label="Date range" hidden={!open}>
        <div className="preset-grid" id="range-presets">
          {presets.map(([label, a, b]) => (
            <button
              key={label}
              type="button"
              className="preset"
              aria-pressed={Math.max(earliest, a) === f && b === t}
              onClick={() => {
                setRange(a, b);
                setOpen(false);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="popover__section">
          <span className="field__hint">Custom range</span>
          <Stepper label="From" value={monthLong(f)} edge="from" onStep={shift} />
          <Stepper label="To" value={monthLong(t)} edge="to" onStep={shift} />
        </div>
      </div>
    </div>
  );
}

interface StepperProps {
  label: string;
  value: string;
  edge: 'from' | 'to';
  onStep: (edge: 'from' | 'to', step: number) => void;
}

function Stepper({ label, value, edge, onStep }: StepperProps) {
  const noun = edge === 'from' ? 'Start' : 'End';
  return (
    <div className="stepper" data-edge={edge}>
      <span className="stepper__label">{label}</span>
      <IconButton
        label={`${noun} one month earlier`}
        icon="left"
        iconSize="xs"
        onClick={() => {
          onStep(edge, -1);
        }}
      />
      <span className="stepper__value">{value}</span>
      <IconButton
        label={`${noun} one month later`}
        icon="right"
        iconSize="xs"
        onClick={() => {
          onStep(edge, 1);
        }}
      />
    </div>
  );
}
